/*
お気に入り一覧（/pages/wishlist）
sections/custom-wishlist.liquid

#1 MultiLikes の App Proxy API からログイン顧客のお気に入り商品を取得して一覧を描画する
#2 商品カードのマークアップはセクション側の template を複製して使う（クラス名は商品一覧と共通）
#3 表示テキストはすべてセクション側の設定JSON（config.texts）から受け取る。JS内に文言を持たない
*/

class CustomWishlist extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.config = this.readConfig();
    if (!this.config) return;

    this.texts = this.config.texts || {};
    this.itemsPerPage = Number(this.config.itemsPerPage) || 28;
    /* 再入荷バッジの判定タグ。管理画面で入力する値のため大文字小文字は区別しない */
    this.restockTag = String(this.config.restockTag || '').trim().toLowerCase();

    this.listElement = this.querySelector('[data-custom-wishlist-list]');
    this.skeletonElement = this.querySelector('[data-custom-wishlist-skeleton]');
    this.emptyElement = this.querySelector('[data-custom-wishlist-empty]');
    this.errorElement = this.querySelector('[data-custom-wishlist-error]');
    this.paginationElement = this.querySelector('[data-custom-wishlist-pagination]');
    this.paginationNavElement = this.querySelector('[data-custom-wishlist-pagination-nav]');
    this.cardTemplate = this.querySelector('[data-custom-wishlist-card]');
    this.prevIconTemplate = this.querySelector('[data-custom-wishlist-icon-prev]');
    this.nextIconTemplate = this.querySelector('[data-custom-wishlist-icon-next]');

    this.currentPage = this.readPageFromUrl();
    this.totalCount = 0;
    this.totalPages = 0;
    this.requestSeq = 0;

    this.handleListClick = this.handleListClick.bind(this);
    this.handlePaginationClick = this.handlePaginationClick.bind(this);
    this.handlePopState = this.handlePopState.bind(this);

    this.listElement.addEventListener('click', this.handleListClick);
    this.paginationNavElement.addEventListener('click', this.handlePaginationClick);
    window.addEventListener('popstate', this.handlePopState);

    /* 顧客メタフィールドで0件と分かっている場合はAPIを呼ばない */
    if (Number(this.config.registeredCount) === 0) {
      this.items = [];
      this.showLoading(false);
      this.render();
      return;
    }

    this.load(this.currentPage);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  readConfig() {
    const configElement = this.querySelector('[data-custom-wishlist-config]');
    if (!configElement) return null;
    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('CustomWishlist: 設定JSONの読み取りに失敗しました', error);
      return null;
    }
  }

  readPageFromUrl() {
    const page = Number(new URLSearchParams(window.location.search).get('page'));
    return Number.isInteger(page) && page > 0 ? page : 1;
  }

  /* #1 一覧の取得 ---------------------------------------------------------- */

  buildListUrl(page) {
    const params = new URLSearchParams({
      customer_id: String(this.config.customerId),
      target_type: 'product',
      mode: 'customer_wishlist',
      limit: String(this.itemsPerPage),
    });

    /* APIの after はカーソルではなくオフセットの数値文字列 */
    if (page > 1) {
      params.set('after', String((page - 1) * this.itemsPerPage));
    }

    return `${this.config.endpoint}?${params.toString()}`;
  }

  /*
    1ページ目は、セクション内のインラインスクリプトが先に投げたリクエストの結果を使う。
    JSファイルのダウンロードと実行を待たないぶん、表示が早くなる
  */
  async fetchList(url, page) {
    const prefetch = window.customWishlistPrefetch;
    if (page === 1 && prefetch && prefetch.url === url && prefetch.promise) {
      const promise = prefetch.promise;
      prefetch.promise = null; /* 2回目以降は使い回さない */
      const prefetched = await promise;
      if (prefetched) return prefetched;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Failed to fetch wishlist: ${response.status}`);

    return response.json();
  }

  async load(page) {
    const seq = ++this.requestSeq;
    this.showLoading(true);
    this.hideError();

    try {
      const result = await this.fetchList(this.buildListUrl(page), page);

      /* 連打などで順序が入れ替わったレスポンスは捨てる */
      if (seq !== this.requestSeq) return;

      this.items = Array.isArray(result.data) ? result.data : [];
      this.totalCount = Number(result.total) || 0;
      this.totalPages = Math.ceil(this.totalCount / this.itemsPerPage);

      /* URLのページ番号が範囲外の場合（別の画面で解除されて件数が減った場合など）は最終ページへ戻す */
      if (this.totalPages > 0 && page > this.totalPages) {
        this.showLoading(false);
        this.goToPage(this.totalPages);
        return;
      }

      this.currentPage = page;
      this.render();
    } catch (error) {
      if (seq !== this.requestSeq) return;
      console.error('CustomWishlist: お気に入りの取得に失敗しました', error);
      this.items = [];
      this.listElement.innerHTML = '';
      this.emptyElement.hidden = true;
      this.renderPagination();
      this.showError(this.texts.error);
    } finally {
      if (seq === this.requestSeq) this.showLoading(false);
    }
  }

  /* #2 描画 ---------------------------------------------------------------- */

  render() {
    this.listElement.innerHTML = '';

    if (this.items.length === 0) {
      this.emptyElement.hidden = false;
      this.renderPagination();
      return;
    }

    this.emptyElement.hidden = true;

    const fragment = document.createDocumentFragment();
    this.items.forEach((item) => fragment.appendChild(this.createCard(item)));
    this.listElement.appendChild(fragment);

    this.renderPagination();
  }

  createCard(item) {
    const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
    const productUrl = item.productHandle ? `/products/${item.productHandle}` : '';

    card.dataset.variantId = item.id || '';
    card.dataset.productId = item.productId || '';

    card.querySelectorAll('[data-wishlist-link]').forEach((link) => {
      link.setAttribute('href', productUrl);
    });

    const image = card.querySelector('[data-wishlist-image]');
    if (item.image) {
      image.src = this.resizeImageUrl(item.image, 600);
      image.srcset = [400, 600, 800]
        .map((width) => `${this.resizeImageUrl(item.image, width)} ${width}w`)
        .join(', ');
      image.sizes = '(max-width: 1219px) 50vw, 25vw';
      image.alt = item.title || '';
    } else {
      image.remove();
    }

    card.querySelector('[data-wishlist-title]').textContent = item.title || '';

    const variantElement = card.querySelector('[data-wishlist-variant]');
    if (item.variantTitle) {
      variantElement.textContent = item.variantTitle;
      variantElement.hidden = false;
    }

    const priceElement = card.querySelector('[data-wishlist-price]');
    const comparePriceElement = card.querySelector('[data-wishlist-compare-price]');
    priceElement.textContent = item.price || '';
    const isOnSale = Boolean(item.compareAtPrice) && item.compareAtPrice !== item.price;
    if (isOnSale) {
      comparePriceElement.textContent = item.compareAtPrice;
      comparePriceElement.hidden = false;
      priceElement.classList.add('price--highlight');
    }

    const isAvailable = item.available === true;

    this.applyTags(card, item, isAvailable);

    /* SOLD OUT / SALE のバッジは商品一覧カードと同じ位置・優先順位で出す */
    const badge = card.querySelector('[data-wishlist-badge]');
    if (!isAvailable) {
      badge.querySelector('span').textContent = this.texts.soldOut;
      badge.hidden = false;
    } else if (isOnSale) {
      badge.querySelector('span').textContent = this.texts.sale;
      badge.hidden = false;
    }

    this.renderActions(card, item, isAvailable);

    return card;
  }

  /* Shopify CDN の画像URLに幅を指定して転送量を抑える */
  resizeImageUrl(imageUrl, width) {
    try {
      const url = new URL(imageUrl, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return imageUrl;
    }
  }

  /* 商品タグ由来のラベル・アイコン（商品一覧カードと同じ仕様） */
  applyTags(card, item, isAvailable) {
    const tags = Array.isArray(item.tags) ? item.tags : [];
    const wornElement = card.querySelector('[data-wishlist-worn]');
    const labelsElement = card.querySelector('[data-wishlist-labels]');
    const iconsElement = card.querySelector('[data-wishlist-icons]');
    const restockElement = card.querySelector('[data-wishlist-restock]');
    const labelTexts = [];
    let hasIcon = false;

    /* 再入荷バッジ。タグが付いていても、そのバリエーションが売り切れなら出さない */
    if (this.restockTag && isAvailable) {
      const hasRestockTag = tags.some(
        (tag) => String(tag).trim().toLowerCase() === this.restockTag
      );
      if (hasRestockTag && restockElement) restockElement.hidden = false;
    }

    tags.forEach((tag) => {
      if (tag === 'icon_setup' || tag === 'icon_rain') {
        const icon = iconsElement.querySelector(`[data-wishlist-icon="${tag}"]`);
        if (icon) {
          icon.hidden = false;
          hasIcon = true;
        }
        return;
      }

      if (tag === 'label__worn') {
        wornElement.querySelector('span').textContent = this.texts.wornBy;
        wornElement.hidden = false;
        return;
      }

      if (tag === 'label__worn__karina') {
        wornElement.querySelector('span').textContent = this.texts.wornByKarina;
        wornElement.hidden = false;
        return;
      }

      if (tag.indexOf('label__') === 0) {
        labelTexts.push(tag.replace('label__', ''));
      }
    });

    if (labelTexts.length > 0) {
      labelTexts.forEach((labelText, index) => {
        const label = document.createElement('span');
        label.className = 'c-product-item__label';
        label.textContent = index === labelTexts.length - 1 ? labelText : `${labelText} / `;
        labelsElement.appendChild(label);
      });
      labelsElement.hidden = false;
    }

    if (hasIcon) iconsElement.hidden = false;
  }

  renderActions(card, item, isAvailable) {
    const actions = card.querySelector('[data-wishlist-actions]');
    const addToCartButton = card.querySelector('[data-wishlist-add-to-cart]');
    const tags = Array.isArray(item.tags) ? item.tags : [];

    /* favorite_noaddtocart タグの商品はカート追加を出さない（MultiLikes と同じ仕様） */
    if (tags.indexOf('favorite_noaddtocart') !== -1) {
      actions.remove();
      return;
    }

    addToCartButton.dataset.variantId = item.id || '';

    if (!isAvailable) {
      addToCartButton.textContent = this.texts.soldOut;
      addToCartButton.disabled = true;
      addToCartButton.classList.add('is-sold-out');
    }
  }

  /* #3 ページャ ------------------------------------------------------------ */

  renderPagination() {
    this.paginationNavElement.innerHTML = '';

    if (this.totalPages <= 1) {
      this.paginationElement.hidden = true;
      return;
    }

    this.paginationElement.hidden = false;

    if (this.currentPage > 1) {
      this.paginationNavElement.appendChild(
        this.createArrow(this.currentPage - 1, this.prevIconTemplate, 'prev')
      );
    }

    this.buildPageParts().forEach((part) => {
      this.paginationNavElement.appendChild(
        part === null ? this.createGap() : this.createPagePart(part)
      );
    });

    if (this.currentPage < this.totalPages) {
      this.paginationNavElement.appendChild(
        this.createArrow(this.currentPage + 1, this.nextIconTemplate, 'next')
      );
    }
  }

  /* 先頭・末尾と現在ページの前後2件を出し、間は null（省略）にする */
  buildPageParts() {
    const parts = [];
    let previous = 0;

    for (let page = 1; page <= this.totalPages; page += 1) {
      const isEdge = page === 1 || page === this.totalPages;
      const isNearCurrent = Math.abs(page - this.currentPage) <= 2;
      if (!isEdge && !isNearCurrent) continue;

      if (previous && page - previous > 1) parts.push(null);
      parts.push(page);
      previous = page;
    }

    return parts;
  }

  createPagePart(page) {
    if (page === this.currentPage) {
      const current = document.createElement('span');
      current.className = 'pagination__nav-item';
      current.setAttribute('aria-current', 'page');
      current.textContent = String(page);
      return current;
    }

    const link = document.createElement('a');
    link.className = 'pagination__nav-item';
    link.href = this.buildPageUrl(page);
    link.dataset.page = String(page);
    link.setAttribute(
      'aria-label',
      String(this.texts.goToPage || '').replace('[[page]]', String(page))
    );
    link.textContent = String(page);
    return link;
  }

  createGap() {
    const gap = document.createElement('span');
    gap.className = 'pagination__nav-item';
    gap.textContent = '…';
    return gap;
  }

  createArrow(page, iconTemplate, rel) {
    const link = document.createElement('a');
    link.className = 'pagination__nav-item';
    link.href = this.buildPageUrl(page);
    link.dataset.page = String(page);
    link.rel = rel;
    link.setAttribute(
      'aria-label',
      rel === 'prev' ? this.texts.previousPage : this.texts.nextPage
    );
    if (iconTemplate) link.appendChild(iconTemplate.content.cloneNode(true));
    return link;
  }

  buildPageUrl(page) {
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    return `${url.pathname}${url.search}`;
  }

  handlePaginationClick(event) {
    const link = event.target.closest('a[data-page]');
    if (!link) return;

    event.preventDefault();
    const page = Number(link.dataset.page);
    if (!page || page === this.currentPage) return;

    window.history.pushState({ page }, '', this.buildPageUrl(page));
    this.goToPage(page);
  }

  handlePopState() {
    this.goToPage(this.readPageFromUrl());
  }

  goToPage(page) {
    /* 範囲外ページの補正やpopstateでもURLと表示をそろえる */
    if (this.buildPageUrl(page) !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState({ page }, '', this.buildPageUrl(page));
    }
    this.load(page);
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* #4 カート追加 ---------------------------------------------------------- */
  /* このページではお気に入りを解除できない（カードに★は出さない） */

  handleListClick(event) {
    const addToCartButton = event.target.closest('[data-wishlist-add-to-cart]');
    if (addToCartButton) this.addToCart(addToCartButton);
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = this.texts.adding;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      /* 422 は在庫不足。カードを売り切れ表示に切り替える */
      if (response.status === 422) {
        button.textContent = this.texts.soldOut;
        button.classList.add('is-sold-out');
        this.markCardAsSoldOut(button.closest('.c-wishlist__item'));
        return;
      }

      if (!response.ok) throw new Error(`Failed to add to cart: ${response.status}`);

      button.textContent = this.texts.added;
      this.notifyCartUpdated();

      window.setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    } catch (error) {
      console.error('CustomWishlist: カートへの追加に失敗しました', error);
      button.textContent = originalText;
      button.disabled = false;
      this.showError(this.texts.addToCartError);
    }
  }

  markCardAsSoldOut(card) {
    if (!card) return;

    /* 一覧表示後に売り切れた場合も再入荷バッジを残さない */
    const restockElement = card.querySelector('[data-wishlist-restock]');
    if (restockElement) restockElement.hidden = true;

    const badge = card.querySelector('[data-wishlist-badge]');
    if (!badge) return;
    badge.querySelector('span').textContent = this.texts.soldOut;
    badge.hidden = false;
  }

  /* テーマ本体（theme.js）がカートドロワー・カート個数の更新に使うイベント */
  notifyCartUpdated() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  /* #5 表示状態の切り替え -------------------------------------------------- */

  /*
    初回はスケルトンを出し、ページ送りなど既にカードが並んでいるときは
    一覧を薄くするだけにして、表示が消えないようにする
  */
  showLoading(isLoading) {
    if (this.skeletonElement) {
      if (isLoading && this.listElement.children.length === 0) {
        this.skeletonElement.hidden = false;
      } else {
        /* 初回の取得が終わればもう使わないので、DOMから取り除く */
        this.skeletonElement.remove();
        this.skeletonElement = null;
      }
    }
    this.classList.toggle('is-loading', isLoading);
  }

  showError(message) {
    if (!message) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    this.errorElement.hidden = true;
  }
}

if (!customElements.get('custom-wishlist')) {
  customElements.define('custom-wishlist', CustomWishlist);
}
